/**
 * Medication Selector Component
 */
import React, { useMemo } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Search, X, Plus, Sun, Sunset, Moon, Calendar, AlertTriangle } from 'lucide-react';

export const MedicationSelector = ({
  medications,
  medicationSearch,
  setMedicationSearch,
  newMedication,
  setNewMedication,
  onAddMedication
}) => {
  // Check if a medication is selected but not added
  const hasPendingMedication = newMedication.medication_id && 
    (newMedication.morning || newMedication.lunch || newMedication.evening || newMedication.once_weekly);

  // Sort and filter medications
  const sortedAndFilteredMedications = useMemo(() => {
    let sorted = [...medications].sort((a, b) => 
      a.commercial.localeCompare(b.commercial, 'ro')
    );
    
    if (medicationSearch.trim()) {
      const search = medicationSearch.toLowerCase().trim();
      sorted = sorted.filter(m => 
        m.commercial.toLowerCase().includes(search) ||
        m.inn?.toLowerCase().includes(search)
      );
    }
    
    return sorted;
  }, [medications, medicationSearch]);

  return (
    <div className="p-4 bg-slate-50 rounded-lg space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Căutați medicament după denumire comercială..."
              value={medicationSearch}
              onChange={(e) => setMedicationSearch(e.target.value)}
              className="pl-10 pr-20"
              data-testid="medication-search"
            />
            {medicationSearch && (
              <button
                type="button"
                onClick={() => setMedicationSearch('')}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {sortedAndFilteredMedications.length}
            </span>
          </div>
          
          {/* Dropdown list */}
          {medicationSearch.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {sortedAndFilteredMedications.length === 0 ? (
                <div className="p-3 text-sm text-slate-500 text-center">
                  Niciun medicament găsit
                </div>
              ) : (
                sortedAndFilteredMedications.slice(0, 50).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      setNewMedication({ 
                        ...newMedication, 
                        medication_id: m.id, 
                        inn_name: m.inn || '',
                        commercial_name: m.commercial || '',
                        form: m.form || '',
                        dose: m.dose || ''
                      });
                      setMedicationSearch('');
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-0 text-sm transition-colors"
                    data-testid={`medication-option-${m.id}`}
                  >
                    <span className="font-medium text-slate-800">{m.commercial}</span>
                    <span className="text-slate-500" title={`${m.form} - ${m.dose}`}>
                      {` - ${m.form} - ${m.dose}`.length > 30 
                        ? ` - ${`${m.form} - ${m.dose}`.substring(0, 30)}...` 
                        : ` - ${m.form} - ${m.dose}`}
                    </span>
                    <span className="block text-xs text-emerald-600" title={m.inn}>
                      {m.inn?.length > 15 ? `${m.inn.substring(0, 15)}...` : m.inn}
                    </span>
                  </button>
                ))
              )}
              {sortedAndFilteredMedications.length > 50 && (
                <div className="p-2 text-xs text-slate-400 text-center bg-slate-50">
                  Tastați pentru a filtra ({sortedAndFilteredMedications.length - 50} ascunse)
                </div>
              )}
            </div>
          )}
          
          {/* Selected medication display */}
          {newMedication.medication_id && !medicationSearch && (
            <div className="mt-2 space-y-2">
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                <div>
                  <span className="font-medium text-emerald-800">{newMedication.commercial_name}</span>
                  <span className="text-emerald-600" title={`${newMedication.form} - ${newMedication.dose}`}>
                    {` - ${newMedication.form} - ${newMedication.dose}`.length > 30 
                      ? ` - ${`${newMedication.form} - ${newMedication.dose}`.substring(0, 30)}...` 
                      : ` - ${newMedication.form} - ${newMedication.dose}`}
                  </span>
                  <span className="block text-xs text-emerald-500" title={newMedication.inn_name}>
                    DCI: {newMedication.inn_name?.length > 15 
                      ? `${newMedication.inn_name.substring(0, 15)}...` 
                      : newMedication.inn_name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setNewMedication({
                      ...newMedication,
                      medication_id: '',
                      inn_name: '',
                      commercial_name: '',
                      form: '',
                      dose: '',
                      ds: '',
                      treatment_days: ''
                    });
                  }}
                  className="text-emerald-600 hover:text-red-500 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="text"
                  placeholder="DS (ex: 1cp x 2/zi)"
                  value={newMedication.ds}
                  onChange={(e) => setNewMedication({ ...newMedication, ds: e.target.value })}
                  data-testid="medication-ds"
                />
                <Input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Zile tratament"
                  value={newMedication.treatment_days}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      setNewMedication({ ...newMedication, treatment_days: val });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === ',') {
                      e.preventDefault();
                    }
                  }}
                  data-testid="medication-days"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Administration schedule */}
      <div className="flex flex-wrap items-center gap-4 md:gap-6">
        <span className="text-sm font-medium text-slate-600">Administrare:</span>
        
        {/* Daily options */}
        <div className={`flex items-center gap-4 ${newMedication.once_weekly ? 'opacity-40' : ''}`}>
          <div className="flex items-center gap-2">
            <Checkbox 
              id="morning"
              checked={newMedication.morning}
              disabled={newMedication.once_weekly}
              onCheckedChange={(checked) => setNewMedication({ ...newMedication, morning: checked })}
              data-testid="med-morning"
            />
            <Label htmlFor="morning" className="flex items-center gap-1 cursor-pointer">
              <Sun className="w-4 h-4 text-amber-500" />
              Dimineața
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox 
              id="lunch"
              checked={newMedication.lunch}
              disabled={newMedication.once_weekly}
              onCheckedChange={(checked) => setNewMedication({ ...newMedication, lunch: checked })}
              data-testid="med-lunch"
            />
            <Label htmlFor="lunch" className="flex items-center gap-1 cursor-pointer">
              <Sunset className="w-4 h-4 text-orange-500" />
              Prânz
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox 
              id="evening"
              checked={newMedication.evening}
              disabled={newMedication.once_weekly}
              onCheckedChange={(checked) => setNewMedication({ ...newMedication, evening: checked })}
              data-testid="med-evening"
            />
            <Label htmlFor="evening" className="flex items-center gap-1 cursor-pointer">
              <Moon className="w-4 h-4 text-indigo-500" />
              Seara
            </Label>
          </div>
        </div>
        
        <span className="text-slate-300">|</span>
        
        {/* Weekly option */}
        <div className={`flex items-center gap-2 ${(newMedication.morning || newMedication.lunch || newMedication.evening) ? 'opacity-40' : ''}`}>
          <Checkbox 
            id="once_weekly"
            checked={newMedication.once_weekly}
            disabled={newMedication.morning || newMedication.lunch || newMedication.evening}
            onCheckedChange={(checked) => setNewMedication({ 
              ...newMedication, 
              once_weekly: checked,
              morning: false,
              lunch: false,
              evening: false
            })}
            data-testid="med-weekly"
          />
          <Label htmlFor="once_weekly" className="flex items-center gap-1 cursor-pointer">
            <Calendar className="w-4 h-4 text-purple-500" />
            O dată pe săptămână
          </Label>
        </div>
        
        <Button 
          type="button" 
          onClick={onAddMedication} 
          className={`ml-auto ${hasPendingMedication ? 'animate-pulse-colors' : ''}`}
          data-testid="add-medication-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Adaugă
        </Button>
        
        {/* Warning message when medication is selected */}
        {hasPendingMedication && (
          <div className="w-full mt-2 p-2 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2 text-amber-700 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Medicament selectat! Apăsați <strong>+ Adaugă</strong> pentru a-l include în listă.</span>
          </div>
        )}
      </div>
    </div>
  );
};
